import Typography from "@vapor/react-extended/ExtendedTypography";
import { Chip, Stack, useTheme } from "@vapor/react-material";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import type { TaxPayerActivityCountersDto } from "../../../../../core/api/use-get-fulfilments/TaxPayerActivityCountersDto";
import { ActivityStatus } from "../../../../../core/dtos/ActivityStatus";
import type { FulfilmentFilterDto } from "../../../../../core/dtos/FulfilmentFilterDto";
import { getLabelFromStatus } from "../../../../components/activity-counter/getLabelFromStatus";

const baseFilter: FulfilmentFilterDto = {
    linked: false,
    completed: false,
    closed: false,
    pending: false,
};

interface FulfimentActivityFilterProps {
    filters: FulfilmentFilterDto;
    hideTotalActivityCounter?: boolean;
    onClickFilter: (filter: FulfilmentFilterDto) => unknown;
    taxPayerActivityCounters: TaxPayerActivityCountersDto;
}

export const FulfimentActivityFilter = ({
    filters,
    hideTotalActivityCounter = false,
    onClickFilter,
    taxPayerActivityCounters: activityCounters,
}: FulfimentActivityFilterProps): ReactElement => {
    const theme = useTheme();
    const { t } = useTranslation();

    return (
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
            <Chip
                variant={
                    !filters.linked &&
                    !filters.closed &&
                    !filters.completed &&
                    !filters.pending
                        ? "outlined"
                        : "filled"
                }
                label={
                    <Typography>
                        {t(
                            "views.home.components.fulfilment_activity_filter.all_filters",
                        )}
                    </Typography>
                }
                onClick={() => onClickFilter({ ...baseFilter })}
            />
            <Chip
                variant={filters.linked ? "outlined" : "filled"}
                label={getLabelFromStatus(
                    ActivityStatus.LINKED,
                    t,
                    activityCounters.linked,
                )}
                onClick={() => {
                    if (!filters.linked) {
                        onClickFilter({
                            ...baseFilter,
                            linked: true,
                        });
                    }
                }}
            />
            <Chip
                variant={filters.pending ? "outlined" : "filled"}
                label={getLabelFromStatus(
                    ActivityStatus.PENDING,
                    t,
                    activityCounters.pending,
                )}
                onClick={() => {
                    if (!filters.pending) {
                        onClickFilter({
                            ...baseFilter,
                            pending: true,
                        });
                    }
                }}
            />
            <Chip
                variant={filters.closed ? "outlined" : "filled"}
                label={getLabelFromStatus(
                    ActivityStatus.CLOSED,
                    t,
                    activityCounters.closed,
                )}
                onClick={() => {
                    if (!filters.closed) {
                        onClickFilter({
                            ...baseFilter,
                            closed: true,
                        });
                    }
                }}
            />
            <Chip
                variant={filters.completed ? "outlined" : "filled"}
                label={getLabelFromStatus(
                    ActivityStatus.COMPLETED,
                    t,
                    activityCounters.completed,
                )}
                onClick={() => {
                    if (!filters.completed) {
                        onClickFilter({
                            ...baseFilter,
                            completed: true,
                        });
                    }
                }}
            />
            {!hideTotalActivityCounter && (
                <Typography
                    color={theme.palette.primary.textSubduedColor}
                    pl={2}
                    variant="label"
                >
                    {`${t(
                        "views.home.components.fulfilment_activity_filter.taxPayers_number",
                    )} ${activityCounters.total}`}
                </Typography>
            )}
        </Stack>
    );
};
